import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useAddOnStep } from '@hooks/useAddOnStep';
import { AddOnCard } from '@components/ProductOffers/configuration/cards';
import { AddOnsStepFooter } from '@components/ProductOffers/configuration/steps/AddOnsStepFooter';
import type { ServiceAddOnsStepProps } from '@components/ProductOffers/configuration/steps/ServiceAddOnsStep/ServiceAddOnsStep.types';
import {
  CardGrid,
  CloseButton,
  ErrorBanner,
} from '@components/ProductOffers/configuration/steps/shared.styles';
import { useConfigurationStore } from '@store/configurationStore';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const EDIT_TITLES: Record<string, string> = {
  Internet: 'Edit Your Internet Add-ons',
  Phone: 'Edit Your Phone Add-ons',
  TV: 'Edit Your TV Add-ons',
};

export const ServiceAddOnsStep = ({
  categoryLabel,
  onClose,
  onSkip,
  onAddToPlan,
  confirmLabel,
  onCancel,
  loading,
}: ServiceAddOnsStepProps) => {
  const {
    addOnCount,
    showError,
    isSelected,
    getQuantity,
    handleToggle,
    handleQuantityChange,
    validateAndSubmit,
    isTmfPlan,
    serviceAddOns,
    allAddOns,
  } = useAddOnStep();

  const editMode = useConfigurationStore((s) => s.editMode);
  const theme = useTheme();

  return (
    <>
      <DialogTitle>
        <Typography variant="title.step" className="StepHeader-title">
          {editMode
            ? (EDIT_TITLES[categoryLabel] ?? `Edit — ${categoryLabel} Plan`)
            : (
                <>
                  <span className="StepHeader-title-full">{`Enhance Your ${categoryLabel} Plan With Optional Add-ons`}</span>
                  <span className="StepHeader-title-compact">{`Add ${categoryLabel} Add-ons`}</span>
                </>
              )}
        </Typography>
        <CloseButton onClick={onClose}>
          <CloseIcon variant="dialogCloseIcon" />
        </CloseButton>
      </DialogTitle>

      <DialogContent dividers={false}>
        <Stack spacing={1} useFlexGap>
          <Typography variant="heading.subsection">Choose Your Add-Ons (Optional)</Typography>
          <Typography variant="body.md" color={theme.novus.borderBlack}>
            {`Your plan and equipment works with ${allAddOns.length} add-ons you might like.`}
          </Typography>
        </Stack>

        {showError && (
          <ErrorBanner>
            <ErrorOutlineIcon variant="errorBannerIcon" />
            <Typography variant="label.field" color={theme.novus.errorRed}>
              Selecting at least 1 add-on is required
            </Typography>
          </ErrorBanner>
        )}

        <CardGrid>
          {serviceAddOns.map((addOn) => (
            <AddOnCard
              key={addOn.code}
              addOn={addOn}
              isSelected={isSelected(addOn.code)}
              quantity={getQuantity(addOn.code)}
              onToggle={() => handleToggle(addOn)}
              onQuantityChange={(qty) => handleQuantityChange(addOn.code, qty)}
            />
          ))}
        </CardGrid>
      </DialogContent>

      <AddOnsStepFooter
        summary={`${addOnCount} Add-Ons Selected`}
        onSkip={onSkip}
        onSubmit={() => validateAndSubmit(onAddToPlan, isTmfPlan ? 0 : 1)}
        confirmLabel={confirmLabel}
        onCancel={onCancel}
        loading={loading}
      />
    </>
  );
};
